import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { userService } from "@/services/breeze/UserService";
import { useAuth } from "@/providers/AuthProvider";
import { SkeletonPage } from "@/components/ui/Skeleton";
import { Unauthorized } from "@/components/ui/Unauthorized";

const withPermissionRequired = <P extends object>(
  permissionName: string,
  WrappedComponent: React.ComponentType<P>,
) => {
  const ProtectedComponent = (props: P) => {
    const { currentTenant, safeNavigate } = useAuth();

    // Cached per tenant, so moving between pages behind this guard doesn't re-check on every mount.
    const { data: isAuthorized, isPending } = useQuery({
      queryKey: ["my-permission", currentTenant?.id, permissionName],
      queryFn: () => userService.checkMyUserHasPermission(permissionName),
      enabled: !!currentTenant?.id,
    });

    useEffect(() => {
      if (!currentTenant?.id) safeNavigate("/unauthorized");
    }, [currentTenant?.id, safeNavigate]);

    if (!currentTenant?.id || isPending) return <SkeletonPage />;

    return isAuthorized ? (
      <WrappedComponent {...props} />
    ) : (
      <Unauthorized
        message={`You don't have the required permission: ${permissionName}`}
      />
    );
  };

  return ProtectedComponent;
};

export default withPermissionRequired;
